import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { safeMarkdownComponents } from '../SafeMarkdown';
import client from '../../api/client';
import { extractMarkdown, agentLabel, isAttemptLimit, statusLabel } from '../../lib/format';
import { DETAIL_PATH, formatClock, formatDuration, kindLabel } from '../../lib/trajectory-record';

function Field({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="insp-field">
      <div className="insp-field-label">{label}</div>
      <div className="insp-field-value">{children}</div>
    </div>
  );
}

function Json({ value }) {
  if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) return null;
  return <pre className="insp-json">{JSON.stringify(value, null, 2)}</pre>;
}

/**
 * Local to the Trajectory tab on purpose: selecting a row must not change any
 * other view's state. The full payload is fetched per record, so a large
 * artifact or rationale never rides along in the ledger response.
 */
export default function TrajectoryInspector({ rec, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!rec) return;
    let stale = false;
    const path = DETAIL_PATH[rec.source_kind]?.(rec.source_id);
    if (!path) return;
    setLoading(true); setError(null); setDetail(null);
    client.get(path)
      .then(r => { if (!stale) setDetail(r.data); })
      .catch(e => { if (!stale) setError(e); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [rec]);

  if (!rec) {
    return (
      <div className="traj-inspector traj-inspector-empty">
        Выберите запись, чтобы увидеть детали
      </div>
    );
  }

  return (
    <div className="traj-inspector">
      <div className="insp-header">
        <div>
          <span className={`traj-kind traj-kind-${rec.kind}`}>{kindLabel(rec.kind)}</span>
          <span className="insp-title">#{rec.seq} · {rec.label}</span>
        </div>
        <button className="insp-close" onClick={onClose} aria-label="Закрыть">×</button>
      </div>

      <div className="insp-body">
        <div className="insp-timing">
          <Field label="Начало">{formatClock(rec.started_at || rec.ts)}</Field>
          <Field label="Длительность">{formatDuration(rec.duration_ms)}</Field>
          <Field label="Turn">{rec.turn}</Field>
          <Field label="Статус">{rec.status === 'stopped' ? statusLabel(rec.status) : rec.status}</Field>
          <Field label="Агент">{rec.agent_name ? agentLabel(rec.agent_name) : null}</Field>
        </div>

        {loading && <div className="text-muted">Загрузка деталей…</div>}
        {error && <div className="insp-error">Не удалось загрузить детали: {error.message}</div>}

        {detail && rec.source_kind === 'task' && (
          <>
            <Field label="Цель">{detail.objective}</Field>
            <Field label="Причина">{detail.reason}</Field>
            <Field label="Итерация / цикл / раунд отдела">
              {`I${detail.iteration} · C${detail.cycle} · R${detail.dept_round}`}
            </Field>
            <Field label="Попытки">{`${detail.retry_count}/${detail.max_retries}`}</Field>
            {isAttemptLimit(detail.last_error) ? (
              <Field label="Причина остановки">{detail.last_error}</Field>
            ) : detail.status !== 'done' && detail.last_error && (
              <Field label="Ошибка"><span className="insp-error">{detail.last_error}</span></Field>
            )}
            <Field label="Входные артефакты">{detail.input_artifact_ids?.join(', ')}</Field>
            <Field label="Выходные артефакты">{detail.output_artifact_ids?.join(', ')}</Field>
            <Json value={detail.expected_output} />
          </>
        )}

        {detail && rec.source_kind === 'artifact' && (
          <>
            <Field label="Тип">{`${detail.artifact_type} · v${detail.version}`}</Field>
            <Field label="Автор">{agentLabel(detail.produced_by)}</Field>
            <Field label="Уверенность">{detail.confidence}</Field>
            {detail.content_markdown && (
              <Field label="Содержимое">
                <div className="markdown insp-markdown">
                  <ReactMarkdown components={safeMarkdownComponents}>{extractMarkdown(detail.content_markdown)}</ReactMarkdown>
                </div>
              </Field>
            )}
            <Json value={detail.content_json} />
            <Json value={detail.meta} />
          </>
        )}

        {detail && rec.source_kind === 'decision_record' && (
          <>
            <Field label="Кто решил">{agentLabel(detail.decided_by)}</Field>
            <Field label="Тип решения">{detail.decision_type}</Field>
            <Field label="Заголовок">{detail.title}</Field>
            {/* The orchestrator's own reasoning text. Until now this was
                write-only from the API's perspective. */}
            <Field label="Обоснование">
              <div className="insp-rationale">{detail.rationale}</div>
            </Field>
            <Field label="Что видел на входе">{detail.input_artifact_ids?.join(', ')}</Field>
            <Field label="Создал задачи">{detail.created_task_ids?.join(', ')}</Field>
            <Json value={detail.meta} />
          </>
        )}

        {detail && rec.source_kind === 'human_request' && (
          <>
            <Field label="Вопрос">{detail.question}</Field>
            <Field label="Контекст">{detail.context_summary}</Field>
            <Field label="Ответ">{detail.answer}</Field>
            <Field label="Канал">{detail.answered_via}</Field>
            <Json value={detail.options} />
          </>
        )}
      </div>
    </div>
  );
}
